import Alpine from "alpinejs";
import { HeygenAvatarManager } from "@src/js/avatar-managers/HeygenAvatarManager";
import { AudioInputManager, AudioEvents } from "@src/js/runner/AudioInputManager";
import { OpenAIManager } from "@src/js/runner/OpenAIManager";
import { getQueryParam } from "@src/js/utils/http";
import { AssistantService } from "@src/js/services/assistantService";
import { AssistantTranscriptService } from "@src/js/services/assistantTranscriptService";
import { ReadyPlayerAzureAssistantManager } from "@src/js/avatar-managers/ReadyPlayerAzureAssistantManager";
import { TimerManager, TimerManagerEvents } from "@src/js/utils/TimerManager";
let avatarManager = null;
document.addEventListener('alpine:init', () => {
    Alpine.data("runner", () => ({
        assistant: {},
        audioInputManager: {},
        openAIManager: {},
        timerManager: null,
        avatarInitialized: false,
        isSpeaking: false,
        showTranscript: true,
        avatarLoading: false,
        error: {},
        remainingSeconds: 0,
        followUpAssistantShareKey: null,
        INACTIVITY_TIMEOUT_MS: 3 * 60 * 1000,
        sessionId: null,
        destroyed: false,
        locked: false,
        async init() {
            let shareKey = getQueryParam("key");
            if (!this.followUpAssistantShareKey) {
                if (shareKey) {
                    try {
                        const assistant = await AssistantService.getByShareKey(shareKey);
                        console.log("Loading assistant with share key:", shareKey, assistant);
                        this.assistant = assistant;
                        if (window.location.pathname.includes('/assistants/shared.html') && assistant.locked) {
                            this.locked = true;
                            this.error.message = 'This assistant is locked. Please contact the owner for access.';
                            console.warn("Assistant is locked, cannot proceed:", assistant);
                        }
                    }
                    catch (error) {
                        console.error('Failed to load assistant:', error);
                    }
                }
            }
            else {
                try {
                    const assistant = await AssistantService.getByShareKey(this.followUpAssistantShareKey);
                    this.sessionId = this.openAIManager.getSessionId();
                    console.log("Loading follow up assistant with share key:", this.followUpAssistantShareKey);
                    console.log("session id:", this.sessionId);
                    this.assistant = assistant;
                }
                catch (error) {
                    console.error('Failed to load follow-up assistant:', error);
                }
            }
            this.openAIManager = await OpenAIManager.init(this.assistant);
            if (this.followUpAssistantShareKey && shareKey && this.sessionId) {
                let transcript = await AssistantTranscriptService.getBySessionId(shareKey, this.sessionId);
                console.log('[Transcript loaded]', transcript);
                transcript.chatMessages.forEach(msg => this.openAIManager.getConversationHistory().push(msg));
            }
            this.audioInputManager = new AudioInputManager(this.assistant.language);
            this.audioInputManager.on('final', (transcript) => {
                if (!transcript.trim()) {
                    console.warn("Empty transcript, not sending to OpenAI");
                    return;
                }
                console.log('[FINAL TRANSCRIPT]', transcript);
                if (this.avatarInitialized && avatarManager) {
                    const fillerSentences = this.assistant?.fillerSentences;
                    if (Array.isArray(fillerSentences) && fillerSentences.length > 0) {
                        const randomFiller = fillerSentences[Math.floor(Math.random() * fillerSentences.length)];
                        this.openAIManager.addManualSentence(randomFiller);
                    }
                }
                this.openAIManager.sendMessageAndStartRun(transcript);
            });
            this.audioInputManager.on('interim', (transcript) => {
                // check whether the transcript is empty
                if (!transcript.trim()) {
                    console.warn("Empty interim transcript, not sending to OpenAI");
                    return;
                }
                if (this.openAIManager) {
                    this.openAIManager.interrupt();
                }
                if (this.avatarInitialized && avatarManager) {
                    avatarManager.interrupt();
                }
            });
            this.openAIManager.on('sentence', (notification) => {
                // console.log('[SENTENCE]', notification);
                this.resetInactivityTimer();
                const mappings = this.assistant?.pronunciationMappings;
                if (Array.isArray(mappings)) {
                    for (const mapping of mappings) {
                        const { original, replacement } = mapping;
                        if (original && replacement) {
                            const regex = new RegExp(original, 'gi');
                            notification = notification.replace(regex, replacement);
                        }
                    }
                }
                if (this.avatarInitialized && avatarManager) {
                    avatarManager.pushToSentenceQueue(notification);
                }
                else {
                    console.warn("Avatar not initialized yet, cannot speak");
                }
            });
            this.audioInputManager.on(AudioEvents.SPEAKING, (val) => {
                this.isSpeaking = val;
                console.log('[Alpine] isSpeaking updated:', val);
            });
            // Initialize TimerManager and start inactivity timer
            this.timerManager = new TimerManager(this.INACTIVITY_TIMEOUT_MS, this.assistant.runtimeSeconds || 0);
            this.timerManager.on(TimerManagerEvents.INACTIVITY_TIMEOUT, () => {
                console.warn("Inactivity timeout reached. Closing session.");
                this.closeSession();
            });
            this.timerManager.on(TimerManagerEvents.TIME_UP, () => {
                console.log("⏱️ Runtime limit reached. Closing session.");
                this.closeSession();
            });
            // Start inactivity timer immediately
            this.timerManager.startInactivityTimer();
        },
        async startAvatar() {
            this.destroyed = false;
            this.avatarLoading = true;
            if (this.assistant.avatarType === 'HEYGEN') {
                await this.startHeygenAvatar();
            }
            else if (this.assistant.avatarType === 'READYPLAYER') {
                await this.startReadyplayerAvatar();
            }
            this.avatarLoading = false;
            this.avatarInitialized = true;
            this.audioInputManager.startListening();
            // Start runtime timer when avatar starts (if runtime limit is enabled)
            if (this.assistant.runtimeLimit && this.assistant.runtimeSeconds && this.assistant.runtimeSeconds > 0 && this.timerManager) {
                this.remainingSeconds = this.assistant.runtimeSeconds;
                this.timerManager.startRuntimeTimer();
                this.timerManager.on(TimerManagerEvents.TIME_UP, () => {
                    console.log("⏱️ Runtime limit reached. Closing session.");
                    this.closeSession();
                });
            }
            const intros = this.assistant?.introSentences;
            if (Array.isArray(intros) && intros.length > 0) {
                const intro = intros[Math.floor(Math.random() * intros.length)];
                this.openAIManager.addManualSentence(intro);
            }
        },
        async startHeygenAvatar() {
            let videoElement = this.$refs.avatarVideo;
            if (!videoElement) {
                console.error("Video element not found");
                return;
            }
            const avatarConfig = {
                avatarName: this.assistant.avatarId,
                quality: this.assistant.quality || 'high',
                videoStyle: 'real',
                voice: {
                    emotion: this.assistant.emotion || 'friendly',
                }
            };
            try {
                avatarManager = await HeygenAvatarManager.init(avatarConfig, videoElement);
                this.avatarInitialized = true;
            }
            catch (err) {
                console.error("Failed to initialize avatar:", err);
            }
            finally {
                this.avatarLoading = false;
            }
        },
        async startReadyplayerAvatar() {
            let avatarElement = this.$refs.shakingHeadVideo;
            const manager = new ReadyPlayerAzureAssistantManager(avatarElement, `https://models.readyplayer.me/${this.assistant.readyplayerAvatarId}.glb?morphTargets=ARKit,Oculus+Visemes,mouthOpen,mouthSmile,eyesClosed,eyesLookUp,eyesLookDown&textureSizeLimit=1024&textureFormat=png`, 'germanywestcentral', 'visemes', this.assistant.azureVoiceId || 'de-DE-KatjaNeural');
            await manager.init();
            avatarManager = manager;
            this.avatarInitialized = true;
        },
        resetInactivityTimer() {
            if (this.timerManager) {
                this.timerManager.resetInactivityTimer();
            }
        },
        formattedTime() {
            return this.timerManager?.getFormattedTime() || '00:00';
        },
        async loadFollowUpAssistant() {
            this.destroyed = false;
            this.avatarInitialized = false;
            this.followUpAssistantShareKey = this.assistant.followUpShareId ?? null;
            if (this.openAIManager?.destroy) {
                this.openAIManager.destroy();
            }
            if (this.audioInputManager?.destroy) {
                this.audioInputManager.destroy();
            }
            if (avatarManager?.destroy) {
                await avatarManager.destroy();
            }
            if (this.timerManager?.destroy) {
                this.timerManager.destroy();
                this.timerManager = null;
            }
            await this.init();
        },
        closeSession() {
            this.audioInputManager.destroy();
            this.openAIManager.destroy();
            avatarManager?.destroy();
            this.destroyed = true;
            this.timerManager?.destroy();
        }
    }));
});
